<script setup lang="ts">
import { computed } from 'vue';
import dayjs from 'dayjs';
import { Badge } from '@/components/ui/badge';
import { formatMoney } from '@/utils/money';

const props = withDefaults(defineProps<{
  orderItem: Record<string, any>,
  /**
   * Where the order opens. A listing embedded in another page sends it to a new tab, so
   * the reader keeps the page they were working on.
   */
  target?: '_self' | '_blank',
}>(), {
  target: '_self',
});

const order = computed(() => props.orderItem.order ?? null);

const statusVariant = computed(() => {
  switch (order.value?.status?.value) {
    case 'new':
      return 'green';
    case 'cancelled':
      return 'red';
    default:
      return 'zinc-light';
  }
});

const variantOptions = computed(() => props.orderItem.variant?.options ?? []);

/** The event's own clock, as every other listing renders its local_* dates. */
const createdOn = computed(() => dayjs(props.orderItem.local_created_at ?? props.orderItem.created_at));

const createdDate = computed(() => createdOn.value.format('ll'));

const createdTime = computed(() => createdOn.value.format('LT'));

/**
 * The row opens the order the line sits on, which is where a line is acted upon. The
 * order number stays a real link, so it can be opened in a new tab or reached with a
 * keyboard.
 */
function openOrder(event: MouseEvent) {
  const url = order.value?.links?.edit;

  if (!url || window.getSelection()?.toString()) {
    return;
  }

  if (props.target === '_blank' || event.metaKey || event.ctrlKey) {
    window.open(url, '_blank');

    return;
  }

  window.location.assign(url);
}
</script>

<template>
  <tr :class="order?.links?.edit ? 'tw:cursor-pointer' : ''" @click="openOrder">
    <td class="tw:align-top">
      <a v-if="order?.links?.edit" :href="order.links.edit" :target="props.target" class="tw:font-bold" @click.stop>
        #{{ order.id }}
      </a>
      <span v-else class="tw:text-zinc-400">—</span>
    </td>

    <td class="tw:align-top tw:text-zinc-500">
      <span class="tw:block tw:whitespace-nowrap tw:xl:inline tw:xl:mr-1">{{ createdDate }}</span>
      <span class="tw:block tw:whitespace-nowrap tw:xl:inline">{{ createdTime }}</span>
    </td>

    <td class="tw:align-top">
      <span v-if="orderItem.activity" class="tw:font-medium">{{ orderItem.activity.code }}</span>
      <span v-else class="tw:text-zinc-400">—</span>

      <!-- What the variant was chosen as; two lines of one activity differ only by this. -->
      <div v-if="variantOptions.length" class="tw:mt-1 tw:flex tw:flex-wrap tw:gap-1">
        <Badge v-for="option in variantOptions" :key="option.id" variant="zinc-light" size="sm">
          <span v-if="option.option" class="tw:text-zinc-500">{{ option.option }}&nbsp;:</span>
          {{ option.value }}
        </Badge>
      </div>
    </td>

    <td class="tw:align-top">
      <div class="tw:flex tw:flex-wrap tw:items-center tw:gap-1">
        <Badge v-if="order?.status" :variant="statusVariant">{{ order.status.label }}</Badge>
        <!-- A line removed from an order it still belongs to. -->
        <Badge v-if="orderItem.deleted_at" variant="red">{{ $t('Deleted') }}</Badge>
      </div>
    </td>

    <td class="tw:align-top tw:text-right tw:tabular-nums">
      <span class="tw:font-semibold tw:text-zinc-900">{{ formatMoney(orderItem.price_with_tax) }}</span>
    </td>
  </tr>
</template>
